import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Pencil, Power, Search, Plus, History, Image as ImageIcon, ArrowUp, ArrowDown, ChevronsUpDown, Eye, ShieldAlert } from 'lucide-react';
import LocationForm from './LocationForm';
import LocationImage from '@/components/common/LocationImage';
import { StatusReasonDialog, HistoryDialog } from './EntityDialogs';
import { canWriteLocations, canReactivateMaster, isOwner } from '@/lib/workspaces';
import { useTableStandard, TableToolbar } from '@/lib/tableStandard';
import { MobileRecordCard, MobileRecordList } from '@/components/common/mobile/MobileRecordCard';
import { MobileSortControl } from '@/components/common/mobile/MobileSortControl';
import { OwnerPurgeDialog } from '@/components/admin/OwnerPurgeDialog';

const StatusPill = ({ active }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${active ? 'bg-[#EAF6DD] text-[#4f8d14]' : 'bg-[#FBF3E4] text-[#C8641E]'}`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const LocationManager = () => {
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const canManage = canWriteLocations(user);
  const canReactivate = canReactivateMaster(user);
  const owner = isOwner(user);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [formLocation, setFormLocation] = useState(undefined);
  const [statusTarget, setStatusTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(() => {
    setLoadError('');
    axios.get(`${API}/service-locations`, authHeaders)
      .then((r) => setItems(r.data || []))
      .catch((e) => {
        const code = e?.response?.status;
        setItems([]);
        setLoadError(code
          ? `Unable to load service locations (server error ${code}). This is a system error, not an empty list — please retry.`
          : 'Unable to load service locations (network error). Please retry.');
      });
    axios.get(`${API}/customers`, authHeaders).then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const searchFiltered = items.filter((l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${l.location_name || ''} ${l.customer_name || ''} ${l.contracting_customer_name || ''} ${l.subaccount_name || ''} ${l.city || ''} ${l.state || ''} ${l.account_number || ''} ${l.location_code || ''}`.toLowerCase().includes(q);
  });

  const std = useTableStandard(searchFiltered, { defaultSort: { key: 'customer_name', dir: 1 } });

  const SortHead = ({ label, sortKey, className = '' }) => {
    const active = std.sort.key === sortKey;
    const next = () => std.setSort((s) => (s.key !== sortKey ? { key: sortKey, dir: 1 } : s.dir === 1 ? { key: sortKey, dir: -1 } : { key: null, dir: 0 }));
    return (
      <TableHead className={`sticky top-0 z-10 bg-gray-50 ${className}`}>
        <button type="button" onClick={next} data-testid={`sort-${sortKey}`}
          className="inline-flex items-center gap-1 select-none hover:text-[#0E2A47] transition-colors">
          {label}
          {active
            ? (std.sort.dir === 1 ? <ArrowUp size={13} className="text-[#8FC93A]" /> : <ArrowDown size={13} className="text-[#8FC93A]" />)
            : <ChevronsUpDown size={13} className="text-slate-300" />}
        </button>
      </TableHead>
    );
  };
  const FilterHead = ({ field, className = '' }) => (
    <TableHead className={`sticky top-0 z-10 bg-gray-50 ${className}`}>
      <input value={std.filters[field] || ''} onChange={(e) => std.setFilter(field, e.target.value)}
        placeholder="Filter…" data-testid={`location-colfilter-${field}`}
        className="w-full min-w-[70px] rounded border border-gray-200 px-2 py-1 text-xs font-normal normal-case" />
    </TableHead>
  );


  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Operations</div>
          <h1 className="text-2xl font-extrabold text-[#0E2A47] sm:text-3xl">Service Locations</h1>
          <p className="text-sm text-slate-500 mt-1">Master service location records — {canManage ? 'open a record to configure the geofence, edit or deactivate.' : 'read-only view.'}</p>
        </div>
        {canManage && (
          <button onClick={() => setFormLocation(null)} data-testid="add-location-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] max-sm:min-h-[44px] max-sm:w-full sm:shrink-0">
            <Plus size={16} /> Add Service Location
          </button>
        )}
      </div>

      <div className="relative max-w-sm mb-3">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Search locations…" value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="location-search" />
      </div>

      <TableToolbar std={std} total={items.length} noun="location" idPrefix="locations" />

      {/* Mobile (<lg): structured record cards with property photos. Desktop table preserved below. */}
      <div className="lg:hidden">
        <div className="mb-3">
          <MobileSortControl sort={std.sort} setSort={std.setSort} testid="location-sort-m" options={[
            { key: 'location_name', label: 'Location Name' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'account_number', label: 'Account #' },
            { key: 'location_code', label: 'Location Code' },
            { key: 'city', label: 'City' },
            { key: 'total_sq_ft', label: 'Total Sq Ft' },
            { key: 'status', label: 'Status' },
          ]} />
        </div>
        {std.matchCount === 0
          ? <div className="rounded-xl border border-slate-200 bg-white py-16 text-center text-sm text-slate-400">No locations found.</div>
          : (
            <MobileRecordList testid="locations-cards">
              {std.view.map((l) => {
                const active = l.is_active !== false && (l.status || 'active') === 'active';
                const img = (
                  <LocationImage locationId={l.id} imageUrl={l.image_url} name={l.location_name} variant="thumb"
                    className="h-12 w-16 border border-slate-200" testId={`loc-thumb-m-${l.id}`} iconSize={18} />
                );
                const actions = (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="rounded-md p-1.5 hover:bg-gray-100" data-testid={`location-actions-m-${l.id}`}><MoreHorizontal size={18} className="text-slate-500" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canManage && <DropdownMenuItem onClick={() => setFormLocation(l)} data-testid={`location-edit-m-${l.id}`}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => setHistoryTarget(l)} data-testid={`location-history-m-${l.id}`}><History size={14} className="mr-2" /> View History</DropdownMenuItem>
                      {canManage && active && <DropdownMenuItem onClick={() => setStatusTarget({ entity: l, nextStatus: 'inactive' })} data-testid={`location-toggle-m-${l.id}`}><Power size={14} className="mr-2" /> Deactivate</DropdownMenuItem>}
                      {!active && canReactivate && <DropdownMenuItem onClick={() => setStatusTarget({ entity: l, nextStatus: 'active' })} data-testid={`location-toggle-m-${l.id}`}><Power size={14} className="mr-2" /> Reactivate</DropdownMenuItem>}
                      {!canManage && <DropdownMenuItem disabled data-testid={`location-readonly-m-${l.id}`}><Eye size={14} className="mr-2" /> Read-only access</DropdownMenuItem>}
                      {owner && <DropdownMenuItem onClick={() => setPurgeTarget(l)} data-testid={`location-purge-m-${l.id}`} className="text-red-600 focus:text-red-700"><ShieldAlert size={14} className="mr-2" /> Purge (Owner only)</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
                return (
                  <MobileRecordCard key={l.id} testid={`location-card-${l.id}`}
                    image={img} title={l.location_name}
                    onTitleClick={canManage ? () => setFormLocation(l) : undefined} titleTestid={`location-open-m-${l.id}`}
                    badge={<StatusPill active={active} />}
                    meta={[
                      { label: 'Customer', value: l.customer_name || '—', full: true },
                      { label: 'Account #', value: l.account_number || '—' },
                      { label: 'Location Code', value: l.location_code || '—' },
                      { label: 'City', value: `${l.city || ''}${l.state ? ', ' + l.state : ''}` || '—' },
                      { label: 'Total Sq Ft', value: l.total_sq_ft ? Number(l.total_sq_ft).toLocaleString() : '—' },
                    ]}
                    actions={actions}
                  />
                );
              })}
            </MobileRecordList>
          )}
      </div>

      <Card className="hidden lg:block">
        <CardContent className="p-0">
            <Table data-testid="locations-table" containerClassName="max-h-[70vh] overflow-auto rounded-md">
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="sticky top-0 z-10 bg-gray-50 w-20">Image</TableHead>
                  <SortHead label="Account #" sortKey="account_number" />
                  <SortHead label="Location Code" sortKey="location_code" className="hidden sm:table-cell" />
                  <SortHead label="Location Name" sortKey="location_name" />
                  <SortHead label="Customer" sortKey="customer_name" className="hidden md:table-cell" />
                  <SortHead label="City" sortKey="city" className="hidden lg:table-cell" />
                  <SortHead label="Total Sq Ft" sortKey="total_sq_ft" className="text-right" />
                  <SortHead label="Status" sortKey="status" />
                  <TableHead className="sticky top-0 z-10 bg-gray-50 text-right">Actions</TableHead>
                </TableRow>
                <TableRow className="bg-gray-50">
                  <TableHead className="sticky top-0 z-10 bg-gray-50" />
                  <FilterHead field="account_number" />
                  <FilterHead field="location_code" className="hidden sm:table-cell" />
                  <FilterHead field="location_name" />
                  <FilterHead field="customer_name" className="hidden md:table-cell" />
                  <FilterHead field="city" className="hidden lg:table-cell" />
                  <TableHead className="sticky top-0 z-10 bg-gray-50" />
                  <FilterHead field="status" />
                  <TableHead className="sticky top-0 z-10 bg-gray-50" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {std.view.map((l) => {
                  const active = l.is_active !== false && (l.status || 'active') === 'active';
                  return (
                    <TableRow key={l.id} data-testid={`location-row-${l.id}`}
                      onClick={canManage ? () => setFormLocation(l) : undefined}
                      className={canManage ? 'cursor-pointer' : undefined}>
                      <TableCell>
                        <LocationImage locationId={l.id} imageUrl={l.image_url} name={l.location_name} variant="thumb"
                          className="h-11 w-16 border border-slate-200" testId={`loc-thumb-${l.id}`} iconSize={16} />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">{l.account_number || '—'}</TableCell>
                      <TableCell className="hidden sm:table-cell font-mono text-xs font-semibold text-[#0E2A47]">{l.location_code || '—'}</TableCell>
                      <TableCell className="font-medium text-slate-800">
                        {l.location_name}
                        <span className="block md:hidden text-xs font-normal text-slate-400" data-testid={`location-customer-mobile-${l.id}`}>{l.customer_name || '—'}</span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-slate-500">
                        {l.subaccount_name
                          ? (<><span className="text-slate-700">{l.subaccount_name}</span><span className="block text-xs text-slate-400" data-testid={`location-contracting-${l.id}`}>under {l.contracting_customer_name}</span></>)
                          : (l.customer_name || '—')}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-500">{`${l.city || ''}${l.state ? ', ' + l.state : ''}` || '—'}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-slate-700" data-testid={`location-sqft-${l.id}`}>{l.total_sq_ft ? Number(l.total_sq_ft).toLocaleString() : '—'}</TableCell>
                      <TableCell><StatusPill active={active} /></TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="rounded-md p-1.5 hover:bg-gray-100" data-testid={`location-actions-${l.id}`}>
                              <MoreHorizontal size={18} className="text-slate-500" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canManage && (
                              <DropdownMenuItem onClick={() => setFormLocation(l)} data-testid={`location-edit-${l.id}`}>
                                <Pencil size={14} className="mr-2" /> Edit
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => setHistoryTarget(l)} data-testid={`location-history-${l.id}`}>
                              <History size={14} className="mr-2" /> View History
                            </DropdownMenuItem>
                            {canManage && active && (
                              <DropdownMenuItem onClick={() => setStatusTarget({ entity: l, nextStatus: 'inactive' })}
                                data-testid={`location-toggle-${l.id}`}>
                                <Power size={14} className="mr-2" /> Deactivate
                              </DropdownMenuItem>
                            )}
                            {!active && canReactivate && (
                              <DropdownMenuItem onClick={() => setStatusTarget({ entity: l, nextStatus: 'active' })}
                                data-testid={`location-toggle-${l.id}`}>
                                <Power size={14} className="mr-2" /> Reactivate
                              </DropdownMenuItem>
                            )}
                            {!canManage && (
                              <DropdownMenuItem disabled data-testid={`location-readonly-${l.id}`}>
                                <Eye size={14} className="mr-2" /> Read-only access
                              </DropdownMenuItem>
                            )}
                            {owner && (
                              <DropdownMenuItem onClick={() => setPurgeTarget(l)} data-testid={`location-purge-${l.id}`}
                                className="text-red-600 focus:text-red-700">
                                <ShieldAlert size={14} className="mr-2" /> Purge (Owner only)
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {loadError
              ? <div data-testid="rout-locations-error" className="py-10 px-4 text-center text-red-700 text-sm bg-red-50 border border-red-200 rounded-md">
                  {loadError}{' '}
                  <button onClick={load} className="underline font-medium ml-1" data-testid="rout-locations-retry">Retry</button>
                </div>
              : std.matchCount === 0 && <div className="py-16 text-center text-slate-400 text-sm">No service locations found.</div>}
        </CardContent>
      </Card>

      {formLocation !== undefined && (
        <LocationForm location={formLocation} customers={customers}
          onClose={() => setFormLocation(undefined)} onSaved={load} />
      )}
      {statusTarget && (
        <StatusReasonDialog kind="location" entity={statusTarget.entity} nextStatus={statusTarget.nextStatus}
          onClose={() => setStatusTarget(null)} onDone={load} />
      )}
      {historyTarget && (
        <HistoryDialog kind="location" entity={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}
      {purgeTarget && (
        <OwnerPurgeDialog entityType="service_location" entityId={purgeTarget.id}
          onClose={() => setPurgeTarget(null)} onPurged={load} />
      )}
    </div>
  );
};

export default LocationManager;
